import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowUpRight, ArrowLeft, ArrowRight, Plus } from 'lucide-react';
import { PPF_LAYERS, PPF_PROCESS, PPF_FAQ } from '../data/mock';
import { PPF_OPTIONS, buildQuery, formatMoney } from '../data/experience';
import Reveal from '../components/Reveal';
import VehicleCoverageMap from '../components/VehicleCoverageMap';

const CoveragePicker = () => {
  const [sel, setSel] = useState('full-front');
  const current = PPF_OPTIONS.find((c) => c.id === sel);
  return (
    <div className="grid grid-cols-1 md:grid-cols-12 gap-10 items-center" data-testid="coverage-picker">
      <div className="md:col-span-5">
        <div className="bg-warm border border-[#e6e2dc] py-10 px-6">
          <VehicleCoverageMap active={current.zones} />
        </div>
      </div>
      <div className="md:col-span-7">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          {PPF_OPTIONS.map((c) => (
            <button key={c.id} data-testid={`coverage-${c.id}`} onClick={() => setSel(c.id)}
              className={`opt text-left p-6 border ${sel === c.id ? 'opt-selected' : 'border-[#e6e2dc] hover:border-red'}`}>
              <div className="flex items-center justify-between">
                <span className={`meta ${sel === c.id ? 'text-white/80' : 'text-red'}`}>{c.n}</span>
                {sel === c.id && <span className="tag bg-white text-red">Selected</span>}
              </div>
              <p className="font-display text-2xl mt-6 leading-tight">{c.title}</p>
              <p className={`font-display text-3xl mt-3 ${sel === c.id ? 'text-white' : 'text-red'}`}>From {formatMoney(c.price, true)}</p>
              <p className={`meta mt-4 leading-relaxed normal-case tracking-normal text-[12px] ${sel === c.id ? 'text-white/85' : 'text-[#6b6b6b]'}`}>{c.body}</p>
            </button>
          ))}
        </div>
        <Link to={buildQuery({ ppf: current.id })} data-testid="coverage-build-link" className="btn btn-red mt-6">Build from this coverage <ArrowRight size={14} /></Link>
        <p className="meta mt-5">Published starting prices · verified Sep 4, 2026 · <a className="linkline" href="https://supremexdetailing.ca/paint-protection-film/" target="_blank" rel="noreferrer">source</a></p>
      </div>
    </div>
  );
};

export default function PPF() {
  return (
    <div data-testid="ppf-page">
      {/* Hero */}
      <section className="px-6 md:px-10 pt-32 md:pt-36 pb-16 md:pb-20">
        <div className="flex items-center justify-between mb-10 fade-up">
          <Link to="/" className="meta linkline inline-flex items-center gap-2"><ArrowLeft size={14} /> Home</Link>
          <span className="tag" style={{ background: 'var(--red-soft)', color: 'var(--red)' }}>Paint protection film · XPEL</span>
        </div>
        <h1 className="font-display h-display max-w-[16ch] fade-up" data-testid="ppf-title">
          Protection you notice by <span className="text-red">not noticing it.</span>
        </h1>
        <div className="grid grid-cols-1 md:grid-cols-12 gap-10 mt-10">
          <div className="md:col-span-6 md:col-start-7 fade-up fade-up-2">
            <p className="body-lg">
              Film should follow the bodywork, disappear at a normal viewing distance and protect the finish without changing why you bought the car. We install XPEL ULTIMATE PLUS and STEALTH at our Ottawa studio, backed by a 10-year manufacturer warranty.
            </p>
          </div>
        </div>
      </section>

      {/* Coverage picker */}
      <section className="px-6 md:px-10 py-20 md:py-28 border-t border-[#e6e2dc]">
        <Reveal className="grid grid-cols-1 md:grid-cols-12 gap-10 mb-12">
          <div className="md:col-span-5"><p className="eyebrow">01 / Coverage</p></div>
          <div className="md:col-span-7"><h2 className="font-display h-section">Choose where the road <span className="text-red">hits first.</span></h2></div>
        </Reveal>
        <Reveal><CoveragePicker /></Reveal>
      </section>

      {/* Anatomy */}
      <section className="px-6 md:px-10 py-20 md:py-28 bg-warm">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-10 items-start">
          <Reveal className="md:col-span-6">
            <p className="eyebrow">02 / Anatomy</p>
            <h2 className="font-display h-section mt-6">Four <span className="text-red">layers.</span></h2>
            <div className="aspect-[4/5] mt-8 overflow-hidden card-hover border border-[#e6e2dc]">
              <img src="/brand/aston.jpg" alt="Aston Martin PPF install at SupremeX" className="w-full h-full object-cover card-media" />
            </div>
          </Reveal>
          <div className="md:col-span-6">
            {PPF_LAYERS.map((l, i) => (
              <Reveal key={l.n} delay={i * 70} className="grid grid-cols-12 gap-4 py-8 border-b border-[#e6e2dc] group">
                <div className="col-span-2 meta text-red pt-1">{l.n}</div>
                <div className="col-span-10">
                  <p className="font-display text-2xl md:text-3xl leading-tight">{l.title}</p>
                  <p className="mt-3 body">{l.body}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Process */}
      <section className="px-6 md:px-10 py-20 md:py-28">
        <Reveal className="grid grid-cols-1 md:grid-cols-12 gap-10 mb-12">
          <div className="md:col-span-5"><p className="eyebrow">03 / Process</p></div>
          <div className="md:col-span-7"><h2 className="font-display h-section">Film is only as good as <span className="text-red">the install.</span></h2></div>
        </Reveal>
        <div className="grid grid-cols-1 md:grid-cols-5 border-t border-[#e6e2dc]">
          {PPF_PROCESS.map((s, i) => (
            <Reveal key={s.n} delay={i * 60} className={`p-6 md:p-8 border-b border-[#e6e2dc] ${i < 4 ? 'md:border-r' : ''}`}>
              <p className="meta text-red">{s.n}</p>
              <p className="font-display text-xl md:text-2xl mt-8 leading-tight">{s.title}</p>
              <p className="meta mt-4 normal-case tracking-normal text-[12px] text-[#6b6b6b]">{s.body}</p>
            </Reveal>
          ))}
        </div>
      </section>

      {/* FAQ */}
      <section className="px-6 md:px-10 py-20 md:py-28 bg-warm">
        <Reveal className="grid grid-cols-1 md:grid-cols-12 gap-10 mb-8">
          <div className="md:col-span-5"><p className="eyebrow">04 / Questions</p></div>
          <div className="md:col-span-7"><h2 className="font-display h-section">Know before <span className="text-red">you book.</span></h2></div>
        </Reveal>
        <div className="max-w-4xl ml-auto">
          {PPF_FAQ.map((f) => (
            <details key={f.q} className="faq-item border-b border-[#e6e2dc] py-8 group" data-testid="ppf-faq-item">
              <summary className="flex items-start justify-between gap-6">
                <p className="font-display text-2xl md:text-3xl leading-tight">{f.q}</p>
                <span className="faq-plus mt-2"><Plus size={22} /></span>
              </summary>
              <p className="mt-6 pr-12 body max-w-2xl">{f.a}</p>
            </details>
          ))}
        </div>
        <div className="mt-14 flex flex-col md:flex-row items-start md:items-center justify-between gap-6">
          <p className="body max-w-md">Ready to protect the car? Build an estimate in seconds, or start with a walkaround at the Ottawa studio.</p>
          <div className="flex items-center gap-4">
            <Link to="/build" data-testid="ppf-build-link" className="btn btn-red">Build your package <ArrowUpRight size={14} /></Link>
            <Link to="/#book" className="btn btn-outline">Book</Link>
          </div>
        </div>
      </section>
    </div>
  );
}
